import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { OpeningBalanceSummary } from '../../../services/schemas/loanServiceSchemas/openingBalanceSummary';
import {
  getOpeningBalanceSummary,
  useActivateOpeningBalance,
  useRaiseOpeningBalanceInterest,
  useRecordOpeningBalancePayment,
} from '../../../services/loanService';
import { formatCurrency } from '../../../utils/formatters';
import { toLocalDateString } from '../../../utils/localDate';

/**
 * Collecting an opening balance: activating it, capturing payments, and raising interest.
 *
 * An opening balance is debt a client already had before the LMS existed. It only became a
 * postable account as a side effect of a new drawdown, so a client whose ONLY debt was legacy
 * had no account at all - there was nothing to capture a payment against and nothing to raise
 * interest on. This panel is the dedicated facility the business asked for.
 *
 * It renders under the "set opening balance" form rather than as a separate screen, because
 * the person capturing the balance is the person who then has to collect it.
 */
interface Props {
  customerPartyId: string;
  customerName: string;
}

const OpeningBalanceCollection: React.FC<Props> = ({ customerPartyId, customerName }) => {
  const [summary, setSummary] = useState<OpeningBalanceSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const today = toLocalDateString();

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(today);
  const [paymentRef, setPaymentRef] = useState('');

  // Interest is entered either as rands or as a percentage of what is outstanding. The business
  // works in percentages ("5% on the balance"), and doing that arithmetic by hand each month is
  // where the errors come from.
  const [interestMode, setInterestMode] = useState<'rate' | 'amount'>('rate');
  const [interestValue, setInterestValue] = useState('');
  const [interestDate, setInterestDate] = useState(today);

  const activateMutation = useActivateOpeningBalance();
  const paymentMutation = useRecordOpeningBalancePayment();
  const interestMutation = useRaiseOpeningBalanceInterest();

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setSummary(await getOpeningBalanceSummary(customerPartyId));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load the opening balance');
    } finally {
      setIsLoading(false);
    }
  }, [customerPartyId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleActivate = async () => {
    try {
      await activateMutation.mutateAsync({ customerPartyId });
      toast.success('Opening balance is now on an account and can be collected');
      await loadSummary();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not activate the opening balance');
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter the amount received');
      return;
    }

    try {
      await paymentMutation.mutateAsync({
        data: {
          customerPartyId,
          amount,
          valueDate: paymentDate,
          externalRef: paymentRef.trim() || undefined,
          // Derived from the reference and amount so the same receipt captured twice is
          // rejected as a duplicate rather than reducing the balance again.
          idempotencyKey: `ob-pay-${customerPartyId}-${paymentDate}-${amount.toFixed(2)}-${paymentRef.trim() || 'noref'}`,
          remarks: 'Payment against opening balance',
        },
      });
      toast.success(`${formatCurrency(amount)} captured against the opening balance`);
      setPaymentAmount('');
      setPaymentRef('');
      await loadSummary();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not capture the payment');
    }
  };

  const handleInterest = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = parseFloat(interestValue);
    if (!value || value <= 0) {
      toast.error(interestMode === 'rate' ? 'Enter the interest rate' : 'Enter the interest amount');
      return;
    }

    try {
      await interestMutation.mutateAsync({
        data: {
          customerPartyId,
          ...(interestMode === 'rate' ? { ratePercent: value } : { amount: value }),
          valueDate: interestDate,
          idempotencyKey: `ob-int-${customerPartyId}-${interestDate}-${interestMode}-${value}`,
        },
      });
      toast.success('Interest raised on the opening balance');
      setInterestValue('');
      await loadSummary();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not raise interest');
    }
  };

  if (isLoading) {
    return (
      <div className="card mt-4">
        <div className="card-body text-center py-4">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Loading opening balance…
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-warning mt-4">
        <strong>Could not load the opening balance.</strong> {loadError}
      </div>
    );
  }

  const hasBalance = (summary?.openingBalance ?? 0) > 0;
  if (!summary || !hasBalance) {
    return null;
  }

  const projectedInterest =
    interestMode === 'rate' && parseFloat(interestValue) > 0 && (summary.totalOutstanding ?? 0) > 0
      ? ((summary.totalOutstanding ?? 0) * parseFloat(interestValue)) / 100
      : null;

  return (
    <div className="card mt-4">
      <div className="card-header bg-white">
        <h5 className="mb-0 fw-bold">Collecting this opening balance</h5>
        <small className="text-muted">
          Debt {customerName} already had before the system. Payments and interest are captured
          here, separately from any loan.
        </small>
      </div>

      <div className="card-body">
        {!summary.activated ? (
          <div className="alert alert-warning">
            <h6 className="alert-heading">This balance is not on an account yet</h6>
            <p className="mb-2">
              {formatCurrency(summary.openingBalance ?? 0)} is recorded against this customer, but
              no account carries it — so nothing can be paid against it and no interest can be
              raised on it. This happened because an opening balance only became postable when a
              new loan was disbursed.
            </p>
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleActivate}
              disabled={activateMutation.isPending}
            >
              {activateMutation.isPending ? 'Opening account…' : 'Open an account for this balance'}
            </button>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div className="p-3 bg-light rounded-3 h-100">
                <small className="text-muted d-block">Brought forward</small>
                <span className="fw-bold">{formatCurrency(summary.openingBalance ?? 0)}</span>
                {summary.openingBalanceDate && (
                  <small className="text-muted d-block">{summary.openingBalanceDate}</small>
                )}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="p-3 bg-light rounded-3 h-100">
                <small className="text-muted d-block">Interest raised</small>
                <span className="fw-bold">{formatCurrency(summary.totalInterestRaised ?? 0)}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="p-3 bg-light rounded-3 h-100">
                <small className="text-muted d-block">Paid</small>
                <span className="fw-bold text-success">{formatCurrency(summary.totalPaid ?? 0)}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="p-3 bg-light rounded-3 h-100">
                <small className="text-muted d-block">Still owing</small>
                <span className="fw-bold text-danger">{formatCurrency(summary.totalOutstanding ?? 0)}</span>
                {summary.accountNumber && (
                  <small className="text-muted d-block">{summary.accountNumber}</small>
                )}
              </div>
            </div>
          </div>
        )}

        {summary.activated && (
          <div className="row g-4">
            <div className="col-12 col-lg-6">
              <h6 className="fw-bold">Capture a payment</h6>
              <form onSubmit={handlePayment}>
                <div className="row g-2">
                  <div className="col-6">
                    <label htmlFor="obPaymentAmount" className="form-label">Amount received (R)</label>
                    <input
                      type="number" id="obPaymentAmount" className="form-control"
                      step="0.01" min="0" placeholder="5000.00"
                      value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label htmlFor="obPaymentDate" className="form-label">Value date</label>
                    <input
                      type="date" id="obPaymentDate" className="form-control"
                      value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="obPaymentRef" className="form-label">Bank reference</label>
                    <input
                      type="text" id="obPaymentRef" className="form-control"
                      placeholder="FNB-20260922-0012"
                      value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-primary w-100" disabled={paymentMutation.isPending}>
                      {paymentMutation.isPending ? 'Capturing…' : 'Capture payment'}
                    </button>
                    <small className="form-text text-muted">
                      Applied in the usual order: penalties, fees, interest, then principal.
                    </small>
                  </div>
                </div>
              </form>
            </div>

            <div className="col-12 col-lg-6">
              <h6 className="fw-bold">Raise interest</h6>
              <form onSubmit={handleInterest}>
                <div className="row g-2">
                  <div className="col-12">
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${interestMode === 'rate' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setInterestMode('rate')}
                      >
                        As a percentage
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${interestMode === 'amount' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setInterestMode('amount')}
                      >
                        As a rand amount
                      </button>
                    </div>
                  </div>
                  <div className="col-6">
                    <label htmlFor="obInterestValue" className="form-label">
                      {interestMode === 'rate' ? 'Rate (%)' : 'Amount (R)'}
                    </label>
                    <input
                      type="number" id="obInterestValue" className="form-control"
                      step="0.01" min="0"
                      placeholder={interestMode === 'rate' ? '5' : '2500.00'}
                      value={interestValue} onChange={(e) => setInterestValue(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label htmlFor="obInterestDate" className="form-label">Value date</label>
                    <input
                      type="date" id="obInterestDate" className="form-control"
                      value={interestDate} onChange={(e) => setInterestDate(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-outline-primary w-100" disabled={interestMutation.isPending}>
                      {interestMutation.isPending ? 'Raising…' : 'Raise interest'}
                    </button>
                    {projectedInterest !== null && (
                      <small className="form-text text-muted">
                        {interestValue}% of {formatCurrency(summary.totalOutstanding ?? 0)} ={' '}
                        <strong>{formatCurrency(projectedInterest)}</strong>
                      </small>
                    )}
                    {interestMode === 'amount' && (
                      <small className="form-text text-muted">
                        Legacy debt has no schedule on the system, so the amount is agreed with
                        the client rather than calculated.
                      </small>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpeningBalanceCollection;
